import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { artists, allQuests } from "@/mocks/artistData";
import Navbar from "@/pages/home/components/Navbar";

const categoryFilters = [
  { key: "all", label: "전체", icon: "ri-apps-line", color: "#ffffff" },
  { key: "kpop", label: "K-POP 아이돌", icon: "ri-music-2-line", color: "#ff6eb4" },
  { key: "drama", label: "드라마 제작사", icon: "ri-tv-2-line", color: "#3d9be9" },
  { key: "film", label: "영화 제작사", icon: "ri-movie-2-line", color: "#e84545" },
  { key: "webtoon", label: "웹툰 스튜디오", icon: "ri-book-open-line", color: "#ff8c42" },
];

const categoryColorMap: Record<string, string> = {
  kpop: "#ff6eb4",
  drama: "#3d9be9",
  film: "#e84545",
  webtoon: "#ff8c42",
};

export default function ArtistsPage() {
  const navigate = useNavigate();
  const [followed, setFollowed] = useState<Record<string, boolean>>({});
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const toggleFollow = (id: string) => {
    setFollowed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredArtists = artists.filter((a) => {
    const matchCategory = activeCategory === "all" ? true : a.category === activeCategory;
    const q = searchQuery.trim().toLowerCase();
    const matchSearch = q === ""
      ? true
      : a.name.toLowerCase().includes(q) ||
        a.tagline.toLowerCase().includes(q) ||
        a.genre.some((g) => g.toLowerCase().includes(q)) ||
        a.categoryLabel.toLowerCase().includes(q);
    return matchCategory && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />

      {/* Hero */}
      <div className="relative overflow-hidden pt-24 pb-16">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/44886208fce333b4b8bdf8553277cc02.jpg"
            alt="Creators Hero"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/70 via-[#0a0a0f]/50 to-[#0a0a0f]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 text-sm text-white/40 mb-6">
            <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">홈</Link>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs" />
            </div>
            <span className="text-white/70">크리에이터</span>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#ff6eb4]/20 to-[#ff8c42]/20 border border-white/10">
              <i className="ri-sparkling-line text-xl text-[#ff6eb4]" />
            </div>
            <span className="text-[#ff6eb4] text-sm font-semibold tracking-widest uppercase">All Creators</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-3">
            엔터테인먼트
            <span className="bg-gradient-to-r from-[#ff6eb4] via-[#3d9be9] to-[#ff8c42] bg-clip-text text-transparent ml-3">
              IP 유니버스
            </span>
          </h1>
          <p className="text-white/50 text-base max-w-xl">
            K-POP 아이돌, 드라마 제작사, 영화사, 웹툰 스튜디오 — {artists.length}개의 공식 IP로 나만의 창작물을 만들어보세요.
          </p>
        </div>
      </div>





      {/* Search Bar */}
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <div className="relative">
          <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
            <i className="ri-search-line text-white/40 text-lg" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="아티스트 이름, 장르, 카테고리로 검색..."
            className="w-full bg-white/5 border border-white/10 rounded-2xl pl-11 pr-12 py-3.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/25 focus:bg-white/8 transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute inset-y-0 right-4 flex items-center cursor-pointer text-white/30 hover:text-white/60 transition-colors"
            >
              <i className="ri-close-line text-lg" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="text-white/30 text-xs mt-2 pl-1">
            <span className="text-white/60 font-semibold">{filteredArtists.length}개</span>의 결과 — &quot;{searchQuery}&quot;
          </p>
        )}
      </div>

      {/* Creator Cards */}
      <div className="max-w-7xl mx-auto px-6 pb-24">
        {filteredArtists.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/5 border border-white/10 mb-4">
              <i className="ri-search-line text-2xl text-white/20" />
            </div>
            <p className="text-white/40 text-base font-medium mb-1">검색 결과가 없어요</p>
            <p className="text-white/20 text-sm">&quot;{searchQuery}&quot;에 해당하는 크리에이터를 찾을 수 없어요</p>
            <button
              onClick={() => setSearchQuery("")}
              className="mt-5 px-5 py-2 rounded-full text-sm font-semibold border border-white/15 text-white/50 hover:text-white hover:border-white/30 transition-all cursor-pointer whitespace-nowrap"
            >
              검색 초기화
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredArtists.map((artist) => {
            const questCount = allQuests.filter((q) => q.artistId === artist.id).length;
            const isHovered = hoveredId === artist.id;
            const catColor = categoryColorMap[artist.category] || "#ff6eb4";
            return (
              <div
                key={artist.id}
                className="relative rounded-3xl border overflow-hidden transition-all duration-500 cursor-pointer group"
                style={{ borderColor: isHovered ? `${artist.accentColor}60` : "rgba(255,255,255,0.08)" }}
                onMouseEnter={() => setHoveredId(artist.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => navigate(`/artists/${artist.id}`)}
              >
                {/* Cover BG */}
                <div className="absolute inset-0">
                  <img
                    src={artist.coverImage}
                    alt={artist.name}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f]/95 via-[#0a0a0f]/80 to-[#0a0a0f]/40" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f]/90 via-transparent to-transparent" />
                </div>

                {/* Category Badge */}
                <div className="absolute top-4 right-4 z-10">
                  <span
                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full"
                    style={{ background: `${catColor}20`, color: catColor, border: `1px solid ${catColor}40` }}
                  >
                    {artist.categoryLabel}
                  </span>
                </div>

                {/* Content */}
                <div className="relative z-10 p-8 flex flex-col sm:flex-row gap-6 items-start">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <div
                      className="w-20 h-20 rounded-2xl overflow-hidden border-2 transition-all duration-300"
                      style={{ borderColor: isHovered ? artist.accentColor : `${artist.accentColor}60` }}
                    >
                      <img src={artist.avatar} alt={artist.name} className="w-full h-full object-cover" />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-2xl font-black text-white">{artist.name}</h2>
                      {artist.verified && (
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-verified-badge-fill text-lg" style={{ color: artist.accentColor }} />
                        </div>
                      )}
                    </div>
                    <p className="text-white/50 text-sm mb-3 italic">{artist.tagline}</p>

                    {/* Genre Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {artist.genre.map((g) => (
                        <span
                          key={g}
                          className="px-2.5 py-1 rounded-full text-xs font-medium border"
                          style={{ borderColor: `${artist.accentColor}40`, color: artist.accentColor, background: `${artist.accentColor}12` }}
                        >
                          {g}
                        </span>
                      ))}
                    </div>

                    {/* Stats Row */}
                    <div className="flex items-center gap-5 mb-5">
                      {[
                        { label: "팔로워", value: artist.stats.followers },
                        { label: "IP", value: `${artist.stats.ipCollections}개` },
                        { label: "캠페인", value: `${questCount}개` },
                      ].map((s, i) => (
                        <div key={s.label} className="flex items-center gap-3">
                          <div>
                            <div className="text-white font-bold text-sm">{s.value}</div>
                            <div className="text-white/40 text-xs">{s.label}</div>
                          </div>
                          {i < 2 && <div className="w-px h-6 bg-white/15" />}
                        </div>
                      ))}
                    </div>

                    {/* IP Preview */}
                    <div className="flex gap-2 mb-5">
                      {artist.ipImages.slice(0, 4).map((img, i) => (
                        <div key={i} className="w-12 h-12 rounded-xl overflow-hidden border border-white/10 flex-shrink-0">
                          <img src={img} alt={`IP ${i + 1}`} className="w-full h-full object-cover object-top" />
                        </div>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFollow(artist.id); }}
                        className="px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
                        style={
                          followed[artist.id]
                            ? { background: "rgba(255,255,255,0.1)", color: "white", border: "1px solid rgba(255,255,255,0.3)" }
                            : { background: `linear-gradient(to right, ${artist.accentColor}, ${artist.secondaryColor})`, color: "#0a0a0f" }
                        }
                      >
                        {followed[artist.id] ? (
                          <span className="flex items-center gap-1.5"><i className="ri-check-line" />팔로잉</span>
                        ) : (
                          <span className="flex items-center gap-1.5"><i className="ri-add-line" />팔로우</span>
                        )}
                      </button>

                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
